import {SpecialistWorkspaceRail} from './SpecialistWorkspaceRail';
import {featureWorkspacePage} from './feature-workspace-navigation';
import {SupplierWorkspaceRail} from './SupplierWorkspaceRail';
import {DashboardWorkspaceRail,dashboardWorkspacePath} from './DashboardWorkspaceRail';
import {AdministrationWorkspaceRail,administrationWorkspacePath} from './AdministrationWorkspaceRail';
import {FinanceWorkspaceRail,financeWorkspacePath} from './FinanceWorkspaceRail';
import {InsuranceWorkspaceRail} from './InsuranceWorkspaceRail';
import {ComplianceWorkspaceRail,complianceWorkspacePath} from './ComplianceWorkspaceRail';
import {ContactWorkspaceRail,contactWorkspacePath} from './ContactWorkspaceRail';
import {OperationsWorkspaceRail,operationsWorkspacePath} from './OperationsWorkspaceRail';
import {RepairWorkspaceRail} from './RepairWorkspaceRail';
import {DocumentWorkspaceRail} from './DocumentWorkspaceRail';
import {BuildingManagementRail,buildingManagementPath} from './BuildingManagementRail';
import {LeadWorkspaceRail} from './LeadWorkspaceRail';
import {MailWorkspaceRail} from './MailWorkspaceRail';
import {WorkspaceRailContext} from './WorkspaceRailSlot';
import {useEffect,useState,type ReactNode} from 'react';
import {Navigate,useLocation} from 'react-router';
import {useAuth} from '../auth';
import {AreaSidePanel} from './AreaSidePanel';
import {BuildingContext} from './BuildingContext';
import {workspaceNavigation} from './area-navigation';
import {useStaffSession, useWorkspaceSession} from './StaffSession';
import {withBuildingScope} from './building-scope';
import {RecordBreadcrumbs} from './RecordBreadcrumbs';
import {useBuildingOnboardingProgress} from './useBuildingOnboardingProgress';
import {BuildingReadinessProvider} from './BuildingReadiness';

/** The authorised staff shell owns building chrome, including deep-link/error routes. */
export function BuildingWorkspaceLayout({children,account}:{children:ReactNode;account?:ReactNode}){
 const session=useWorkspaceSession()!;
 return <BuildingReadinessProvider building={session.effectiveScope.kind==='building'?session.effectiveScope.id:undefined}><BuildingWorkspaceContent account={account}>{children}</BuildingWorkspaceContent></BuildingReadinessProvider>;
}
function BuildingWorkspaceContent({children,account}:{children:ReactNode;account?:ReactNode}){
 const location=useLocation();
 const [railSlot,setRailSlot]=useState<HTMLDivElement|null>(null);
 useEffect(()=>{
  if(!location.hash)return;
  let id:string;try{id=decodeURIComponent(location.hash.slice(1));}catch{return;}
  const reveal=()=>{const target=document.getElementById(id);if(!target)return false;let parent=target.closest('details');while(parent){parent.open=true;parent=parent.parentElement?.closest('details')??null;}target.scrollIntoView({block:'start'});target.focus({preventScroll:true});return true;};
  if(reveal())return;
  const observer=new MutationObserver(()=>{if(reveal())observer.disconnect();});observer.observe(document.getElementById('main-content')??document.body,{childList:true,subtree:true});
  return()=>observer.disconnect();
 },[location.pathname,location.hash]);
 const session=useWorkspaceSession()!,auth=useAuth();
 const onboarding=useBuildingOnboardingProgress(session.effectiveScope.kind==='building'?session.effectiveScope.id:undefined);
 const navigation=workspaceNavigation(session,location,auth.config??{},onboarding);
 // One stable tree, so pages never remount when an area gains or loses its side panel.
 const basePanel=navigation.tertiary??{area:navigation.secondary.label,label:navigation.secondary.label.replace(/ navigation$/, ''),sections:[{items:navigation.secondary.items}],active:navigation.secondary.active};
 const buildingManagement=buildingManagementPath(location.pathname);
 const globalSearch=location.pathname==='/search';
 const panel=dashboardWorkspacePath(location.pathname)?{...basePanel,label:'Dashboards'}:administrationWorkspacePath(location.pathname)?{...basePanel,label:'Administration'}:financeWorkspacePath(location.pathname)?{...basePanel,label:'Finance'}:
 (location.pathname.startsWith('/insurance')||location.pathname==='/engines/insurance')?{...basePanel,label:'Insurance'}:
 complianceWorkspacePath(location.pathname)?{...basePanel,label:'Compliance'}:
 buildingManagement?{...basePanel,label:'Building management'}:
 contactWorkspacePath(location.pathname)?{...basePanel,label:'Property & people'}:
 operationsWorkspacePath(location.pathname)?{...basePanel,label:'Operations'}:basePanel;
 return <WorkspaceRailContext.Provider value={railSlot}><div className={'area-workspace approved-workspace'+(globalSearch?' approved-workspace--global':' has-side-panel')}>
  {!globalSearch&&<div className="workspace-rail">
<BuildingContext navigation={{...navigation,secondary:{...navigation.secondary,items:[]}}}/>
<AreaSidePanel navigation={panel}/>
<ContactWorkspaceRail navigation={panel}/>
<DocumentWorkspaceRail navigation={panel}/>
<RepairWorkspaceRail navigation={panel}/>
<SupplierWorkspaceRail navigation={panel}/>
<SpecialistWorkspaceRail navigation={panel}/>
<OperationsWorkspaceRail navigation={panel}/>
<ComplianceWorkspaceRail navigation={panel}/>
<InsuranceWorkspaceRail navigation={panel}/>
<FinanceWorkspaceRail navigation={panel}/>
<AdministrationWorkspaceRail/>
<DashboardWorkspaceRail/>
<BuildingManagementRail navigation={panel} onboardingHref={onboarding?.href}/>
{(location.pathname.startsWith('/communications')||location.pathname==='/engines/communications'||featureWorkspacePage(location.pathname)?.workspace==='communications')&&session.capabilities.includes('communications:view')&&<MailWorkspaceRail navigation={panel}/>}
{(location.pathname.startsWith('/enquiries')||location.pathname==='/engines/new_business')&&session.capabilities.includes('enquiries:view')&&<LeadWorkspaceRail navigation={panel}/>}<div ref={setRailSlot} className="workspace-rail__route"/>
</div>}
<div className="workspace-account">{account}</div>
  <div className={'area-workspace__main'+(featureWorkspacePage(location.pathname)?' alpha-workspace':'')+(buildingManagement?' building-management-workspace':'')}><RecordBreadcrumbs workspaceLabel={globalSearch?'Search':panel.label}/>{children}</div>
 </div></WorkspaceRailContext.Provider>;
}

/** Compatibility routes retain meaningful URL state; no duplicate page implementation. */
export function BuildingContextRedirect({to}:{to:string}){
 const location=useLocation(),session=useStaffSession()!;
 return <Navigate replace to={withBuildingScope(to+location.search+location.hash,session.effectiveScope.kind==='building'?session.effectiveScope.id:undefined)} />;
}
